/**
 * Specification Detail Page
 * @implements FS-004.5.2 - View Specification Details
 * @implements US-029 - Basic Specification Viewing
 *
 * Displays full specification content.
 * Route: /projects/:projectId/specifications/:specId
 */

import {
  Alert,
  Box,
  Breadcrumbs,
  Button,
  CircularProgress,
  Container,
  Link,
  Paper,
  Typography,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  NavigateNext as NavigateNextIcon,
} from '@mui/icons-material';
import { useParams, useNavigate, Link as RouterLink } from 'react-router-dom';
import { useQuery, gql } from '@apollo/client';
import { SpecificationDetail } from '@/components/specification';

/**
 * GraphQL query for fetching project and specification title for breadcrumbs
 */
const GET_PROJECT_AND_SPEC_QUERY = gql`
  query GetProjectAndSpec($projectId: ID!, $specId: ID!) {
    project(id: $projectId) {
      id
      title
      lifecycleState
    }
    specification(id: $specId) {
      id
      formattedNumber
      title
    }
  }
`;

/**
 * Project type from the GraphQL response
 */
interface Project {
  id: string;
  title: string;
  lifecycleState: string;
}

/**
 * Specification type for breadcrumbs
 */
interface SpecificationBreadcrumb {
  id: string;
  formattedNumber: string;
  title: string;
}

/**
 * Response type for GetProjectAndSpec query
 */
interface GetProjectAndSpecResponse {
  project: Project | null;
  specification: SpecificationBreadcrumb | null;
}

/**
 * Variables for GetProjectAndSpec query
 */
interface GetProjectAndSpecVariables {
  projectId: string;
  specId: string;
}

/**
 * SpecificationDetailPage - Page component for viewing specification details
 *
 * @implements FS-004.5.2 - View Specification Details
 * @implements US-029 - Basic Specification Viewing
 *
 * Features:
 * - Fetches project and specification data for breadcrumbs
 * - Displays SpecificationDetail component
 * - Handles loading, error, and 404 states
 * - Provides navigation back to specifications list
 * - "Discuss with PM" navigates to project view
 *
 * Route: /projects/:projectId/specifications/:specId
 */
export const SpecificationDetailPage = () => {
  const { projectId, specId } = useParams<{
    projectId: string;
    specId: string;
  }>();
  const navigate = useNavigate();

  // Fetch project and specification data for breadcrumbs
  const { data, loading, error } = useQuery<
    GetProjectAndSpecResponse,
    GetProjectAndSpecVariables
  >(GET_PROJECT_AND_SPEC_QUERY, {
    variables: { projectId: projectId!, specId: specId! },
    skip: !projectId || !specId,
    fetchPolicy: 'cache-and-network',
  });

  const project = data?.project;
  const specification = data?.specification;

  // Handle "Discuss with PM" click
  // @implements US-029 - PM Integration
  const handleDiscussClick = () => {
    // Navigate to project view which has the conversation
    navigate(`/projects/${projectId}`);
  };

  // Render loading state
  if (loading && !data) {
    return (
      <Container maxWidth="md">
        <Box
          sx={{
            minHeight: '100vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  // Render error state
  if (error) {
    return (
      <Container maxWidth="md">
        <Box sx={{ py: 4 }}>
          <Alert severity="error" sx={{ mb: 3 }}>
            Failed to load data: {error.message}
          </Alert>
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate(`/projects/${projectId}/specifications`)}
          >
            Back to Specifications
          </Button>
        </Box>
      </Container>
    );
  }

  // Render 404 state (project not found)
  if (!project) {
    return (
      <Container maxWidth="md">
        <Box sx={{ py: 4 }}>
          <Paper
            elevation={0}
            sx={{
              p: 4,
              textAlign: 'center',
              border: '1px solid',
              borderColor: 'divider',
            }}
          >
            <Typography variant="h5" gutterBottom>
              Project Not Found
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
              The project you are looking for does not exist or you do not have
              access to it.
            </Typography>
            <Button
              variant="contained"
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate('/projects')}
            >
              Back to Projects
            </Button>
          </Paper>
        </Box>
      </Container>
    );
  }

  // Render 404 state (specification not found)
  if (!specification) {
    return (
      <Container maxWidth="md">
        <Box sx={{ py: 4 }}>
          <Breadcrumbs
            separator={<NavigateNextIcon fontSize="small" />}
            sx={{ mb: 3 }}
          >
            <Link
              component={RouterLink}
              to="/projects"
              color="inherit"
              underline="hover"
            >
              Projects
            </Link>
            <Link
              component={RouterLink}
              to={`/projects/${projectId}`}
              color="inherit"
              underline="hover"
            >
              {project.title}
            </Link>
            <Link
              component={RouterLink}
              to={`/projects/${projectId}/specifications`}
              color="inherit"
              underline="hover"
            >
              Specifications
            </Link>
            <Typography color="text.primary">Not Found</Typography>
          </Breadcrumbs>
          <Paper
            elevation={0}
            sx={{
              p: 4,
              textAlign: 'center',
              border: '1px solid',
              borderColor: 'divider',
            }}
          >
            <Typography variant="h5" gutterBottom>
              Specification Not Found
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
              The specification you are looking for does not exist or you do not
              have access to it.
            </Typography>
            <Button
              variant="contained"
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate(`/projects/${projectId}/specifications`)}
            >
              Back to Specifications
            </Button>
          </Paper>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="md">
      <Box sx={{ py: 4 }}>
        {/* Breadcrumbs */}
        {/* @implements US-029 - Navigation back to list */}
        <Breadcrumbs
          separator={<NavigateNextIcon fontSize="small" />}
          sx={{ mb: 3 }}
        >
          <Link
            component={RouterLink}
            to="/projects"
            color="inherit"
            underline="hover"
          >
            Projects
          </Link>
          <Link
            component={RouterLink}
            to={`/projects/${projectId}`}
            color="inherit"
            underline="hover"
          >
            {project.title}
          </Link>
          <Link
            component={RouterLink}
            to={`/projects/${projectId}/specifications`}
            color="inherit"
            underline="hover"
          >
            Specifications
          </Link>
          <Typography color="text.primary">
            {specification.formattedNumber}
          </Typography>
        </Breadcrumbs>

        {/* Specification Detail */}
        <SpecificationDetail
          specificationId={specId!}
          projectId={projectId!}
          showDiscussButton={true}
          onDiscussClick={handleDiscussClick}
        />

        {/* Back to Specifications Link */}
        <Box sx={{ mt: 3 }}>
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            component={RouterLink}
            to={`/projects/${projectId}/specifications`}
          >
            Back to Specifications
          </Button>
        </Box>
      </Box>
    </Container>
  );
};
